<script lang="ts">
  import { api } from '$shared/api';
  import type { AiScoreBreakdown } from '$lib/types';
  import AiScoreBreakdownComponent from '$components/shared/AiScoreBreakdown.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';

  let { trackId }: { trackId: string } = $props();

  let score = $state<AiScoreBreakdown | null>(null);
  let loading = $state(true);
  let error = $state('');

  $effect(() => {
    api
      .get<AiScoreBreakdown>(`/tracks/${trackId}/ai-score`)
      .then((d) => {
        score = d;
        loading = false;
      })
      .catch((e) => {
        error = e?.message || 'Score not available';
        loading = false;
      });
  });
</script>

<div>
  {#if loading}
    <h1>AI Score</h1>
    <LoadingSkeleton lines={6} />
  {:else if error}
    <h1>AI Score</h1>
    <p class="empty">{error}</p>
    <div class="back"><a href="#/duels">&larr; Browse Duels</a></div>
  {:else if score}
    <h1>AI Score — {score.track_name || 'Track'}</h1>
    <p class="meta">
      by <a href="#/users/{score.username}">{score.username}</a>
      in <a href="#/duels/{score.duel_id}/result">{score.duel_name}</a>
    </p>

    <div class="card card--no-hover">
      <AiScoreBreakdownComponent {score} />
    </div>

    {#if score.acoustic}
      <div class="card card--no-hover acoustic-card">
        <h3 class="acoustic-title">Audio Fingerprint</h3>
        <div class="acoustic-grid">
          {#if score.acoustic.bpm}
            <div class="acoustic-stat">
              <span class="acoustic-label">BPM</span>
              <span class="acoustic-value">{Math.round(score.acoustic.bpm)}</span>
            </div>
          {/if}
          {#if score.acoustic.key_signature}
            <div class="acoustic-stat">
              <span class="acoustic-label">Key</span>
              <span class="acoustic-value">{score.acoustic.key_signature}</span>
            </div>
          {/if}
          {#if score.acoustic.loudness_lufs != null}
            <div class="acoustic-stat">
              <span class="acoustic-label">Loudness</span>
              <span class="acoustic-value">{score.acoustic.loudness_lufs} LUFS</span>
            </div>
          {/if}
          {#if score.acoustic.danceability != null}
            <div class="acoustic-stat">
              <span class="acoustic-label">Danceability</span>
              <div class="acoustic-bar">
                <div class="acoustic-fill" style="width: {score.acoustic.danceability * 100}%"></div>
              </div>
            </div>
          {/if}
          {#if score.acoustic.energy != null}
            <div class="acoustic-stat">
              <span class="acoustic-label">Energy</span>
              <div class="acoustic-bar">
                <div class="acoustic-fill acoustic-fill--energy" style="width: {score.acoustic.energy * 100}%"></div>
              </div>
            </div>
          {/if}
          {#if score.acoustic.dynamic_range_db != null}
            <div class="acoustic-stat">
              <span class="acoustic-label">Dynamic Range</span>
              <span class="acoustic-value">{score.acoustic.dynamic_range_db} dB</span>
            </div>
          {/if}
          {#if score.acoustic.genre_tags?.length}
            <div class="acoustic-stat acoustic-stat--wide">
              <span class="acoustic-label">Genre Tags</span>
              <div class="tag-list">
                {#each score.acoustic.genre_tags as tag}
                  <span class="tag">{tag}</span>
                {/each}
              </div>
            </div>
          {/if}
        </div>
        <p class="acoustic-source">
          Source: {score.acoustic.source === 'essentia'
            ? 'Audio Analysis (Essentia)'
            : score.acoustic.source === 'acousticbrainz'
              ? 'AcousticBrainz Database'
              : score.acoustic.source === 'youtube_api'
                ? 'YouTube Metadata'
                : 'SoundCloud Metadata'}
          {#if score.acoustic.source === 'acousticbrainz' && score.acoustic.match_confidence}
            <span class="match-confidence">
              (matched via MusicBrainz · {Math.round(score.acoustic.match_confidence * 100)}% confidence)
            </span>
          {/if}
        </p>
      </div>
    {/if}

    <div class="back">
      <a href="#/duels/{score.duel_id}/result">&larr; Back to Results</a>
    </div>
  {/if}
</div>

<style>
  .meta {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-bottom: 1.5rem;
  }
  .meta a {
    color: var(--cyan);
  }
  .empty {
    color: var(--text-secondary);
  }
  .back {
    margin-top: 1.5rem;
  }
  .back a {
    font-size: 0.875rem;
    color: var(--cyan);
  }

  .acoustic-card {
    margin-top: 1rem;
  }
  .acoustic-title {
    font-size: 1rem;
    font-weight: 700;
    margin: 0 0 1rem;
    color: var(--text);
  }
  .acoustic-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem 1.5rem;
  }
  .acoustic-stat {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .acoustic-stat--wide {
    grid-column: 1 / -1;
  }
  .acoustic-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--text-muted);
    letter-spacing: 0.05em;
  }
  .acoustic-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text);
  }
  .acoustic-bar {
    height: 6px;
    background: var(--surface-2);
    border-radius: 3px;
    overflow: hidden;
  }
  .acoustic-fill {
    height: 100%;
    border-radius: 3px;
    background: var(--purple);
    transition: width 0.6s ease;
  }
  .acoustic-fill--energy {
    background: var(--pink);
  }
  .tag-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
  }
  .tag {
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 500;
    background: rgba(139, 92, 246, 0.15);
    color: #8b5cf6;
  }
  .acoustic-source {
    font-size: 0.7rem;
    color: var(--text-muted);
    margin-top: 0.5rem;
  }
  .match-confidence {
    color: var(--text-muted);
    font-style: italic;
  }
  @media (max-width: 480px) {
    .acoustic-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
